import React, { useState, useEffect } from 'react';
import { 
  User, Phone, MapPin, Calendar, Package, DollarSign, 
  TrendingUp, MessageSquare, Clock, CheckCircle, XCircle,
  Image as ImageIcon, FileText
} from 'lucide-react';
import SalesOrderModal from './SalesOrderModal';

const API_URL = process.env.REACT_APP_API_URL;

function SalesChatInfo({ chat, userId, onChatUpdate }) {
  const [orders, setOrders] = useState([]);
  const [aiInteractions, setAiInteractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    fetchChatDetails();
  }, [chat.id]);

  const fetchChatDetails = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/sales/chats/${chat.id}`);
      const data = await response.json();
      
      setOrders(data.orders || []);
      setAiInteractions(data.ai_interactions || []);
    } catch (error) {
      console.error('Error fetching chat details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getOrderStatusColor = (status) => {
    const colors = {
      'pending': 'bg-yellow-100 text-yellow-700',
      'confirmed': 'bg-blue-100 text-blue-700',
      'preparing': 'bg-purple-100 text-purple-700',
      'in_transit': 'bg-indigo-100 text-indigo-700',
      'delivered': 'bg-green-100 text-green-700',
      'cancelled': 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getOrderStatusIcon = (status) => {
    const icons = {
      'pending': Clock,
      'confirmed': CheckCircle,
      'preparing': Package,
      'in_transit': TrendingUp,
      'delivered': CheckCircle,
      'cancelled': XCircle
    };
    const Icon = icons[status] || Clock;
    return <Icon className="w-4 h-4" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-PE', { 
      day: '2-digit', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const aiContext = chat.ai_context || {};
  const interestedProducts = aiContext.interested_products || [];

  return (
    <>
      <div className="p-6">
        {/* Customer Info */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Información del Cliente
          </h3>
          
          <div className="space-y-3">
            <div className="flex items-center space-x-3 text-sm">
              <User className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-gray-500">Nombre</p>
                <p className="font-medium text-gray-900">
                  {chat.customer_name || 'No proporcionado'}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3 text-sm">
              <Phone className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-gray-500">Teléfono</p>
                <p className="font-medium text-gray-900">{chat.phone_number}</p>
              </div>
            </div>

            <div className="flex items-center space-x-3 text-sm">
              <Calendar className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-gray-500">Primer contacto</p>
                <p className="font-medium text-gray-900">
                  {formatDate(chat.created_at)}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3 text-sm">
              <MessageSquare className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-gray-500">Último mensaje</p>
                <p className="font-medium text-gray-900">
                  {formatDate(chat.last_message_at)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* AI Context */}
        {Object.keys(aiContext).length > 0 && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg">
            <h3 className="text-sm font-semibold text-blue-900 mb-3">
              Contexto de IA
            </h3>
            <div className="space-y-2 text-sm">
              {aiContext.delivery_type && (
                <div>
                  <span className="text-blue-700 font-medium">Tipo de entrega:</span>
                  <span className="ml-2 text-blue-900">
                    {aiContext.delivery_type === 'lima' ? '📦 Lima' : '🚚 Provincia'}
                  </span>
                </div>
              )}
              {aiContext.delivery_district && (
                <div>
                  <span className="text-blue-700 font-medium">Distrito:</span>
                  <span className="ml-2 text-blue-900">{aiContext.delivery_district}</span>
                </div>
              )}
              {aiContext.delivery_city && (
                <div>
                  <span className="text-blue-700 font-medium">Ciudad:</span>
                  <span className="ml-2 text-blue-900">{aiContext.delivery_city}</span>
                </div>
              )}
              {interestedProducts.length > 0 && (
                <div>
                  <span className="text-blue-700 font-medium">Productos de interés:</span>
                  <span className="ml-2 text-blue-900">{interestedProducts.length}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Orders */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center justify-between">
            <span>Órdenes ({orders.length})</span>
            {orders.length > 0 && (
              <Package className="w-5 h-5 text-[#B1336E]" />
            )}
          </h3>

          {loading ? (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#B1336E] mx-auto"></div>
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Sin órdenes aún</p>
            </div>
          ) : (
            <div className="space-y-3">
              {orders.map(order => (
                <div
                  key={order.id}
                  onClick={() => setSelectedOrder(order)}
                  className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">
                        {order.order_number}
                      </p>
                      <p className="text-xs text-gray-500">
                        {formatDate(order.created_at)}
                      </p>
                    </div>
                    <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs ${getOrderStatusColor(order.status)}`}>
                      {getOrderStatusIcon(order.status)}
                      <span>{order.status}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">
                      {order.items?.length || 0} items
                    </span>
                    <span className="font-semibold text-[#B1336E]">
                      S/ {parseFloat(order.total_amount).toFixed(2)}
                    </span>
                  </div>

                  {order.delivery_type === 'provincia' && order.requires_prepayment && (
                    <div className="mt-2 pt-2 border-t border-gray-100">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-gray-500">Adelanto:</span>
                        <span className={`font-medium ${
                          order.prepayment_status === 'confirmed' 
                            ? 'text-green-600' 
                            : 'text-yellow-600'
                        }`}>
                          S/ {parseFloat(order.prepayment_amount || 0).toFixed(2)}
                          {order.prepayment_status === 'confirmed' ? ' ✓' : ' ⏳'}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* AI Interactions Summary */}
        {aiInteractions.length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Interacciones con IA
            </h3>
            <div className="space-y-2">
              {aiInteractions.slice(0, 5).map(interaction => (
                <div
                  key={interaction.id}
                  className="p-3 bg-gray-50 rounded-lg text-sm"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-gray-700">
                      {interaction.detected_intent || 'unknown'}
                    </span>
                    {interaction.confidence_score && (
                      <span className="text-xs text-gray-500">
                        {(interaction.confidence_score * 100).toFixed(0)}%
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate">
                    {interaction.user_message}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="space-y-2">
          <button
            onClick={fetchChatDetails}
            className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium text-sm"
          >
            Actualizar Información
          </button>
        </div>
      </div>

      {/* Order Modal */}
      {selectedOrder && (
        <SalesOrderModal
          order={selectedOrder}
          onClose={() => setSelectedOrder(null)}
          onUpdate={fetchChatDetails}
        />
      )}
    </>
  );
}

export default SalesChatInfo;